import { centroid } from '@spotnote/shared/geometry'
import type { NoteType, WidgetConfig } from '@spotnote/shared/types'
import { ApiError, createApi, toWirePayload, type Api } from './api'
import { captureSession, withDeadline, RASTER_WAIT_MS, type SessionCapture } from './capture'
import { h, replace } from './dom'
import { Overlay } from './overlay'
import { STYLES } from './styles'
import { Store, type ComposerTarget, type LocalNote } from './state'
import { forgetReviewToken, readReviewToken } from './token'
import { attachUnsavedGuard } from './unsaved'
import { detectWidgetLocale, setWidgetLocale, t, type WidgetLocale } from './i18n'
import { createCaptureSurface } from './tools'
import { renderComposer } from './ui/composer'
import { renderLauncher } from './ui/launcher'
import { renderReview, renderSent } from './ui/review'
import { hintText, renderToolbar } from './ui/toolbar'

const HOST_ID = 'spotnote-root'

function readScriptConfig(): { key: string; base: string } | null {
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-key][src*="w.js"]')

  const key = script?.dataset.key
  if (!key || !script?.src) return null

  return { key, base: new URL(script.src).origin }
}

function pageMetrics() {
  const doc = document.documentElement
  return {
    page_url: window.location.href,
    page_width: Math.max(doc.scrollWidth, doc.clientWidth, 1),
    page_height: Math.max(doc.scrollHeight, doc.clientHeight, 1),
    viewport_w: Math.max(window.innerWidth, 1),
    viewport_h: Math.max(window.innerHeight, 1),
    user_agent: navigator.userAgent,
  }
}

/**
 * The loader step, and the whole prod-safety story:
 *
 * The widget is invisible by default, on every page, production included.
 * Waking up requires an invitation token (from the review link's `?feedback=`
 * parameter, or remembered in localStorage from a previous visit) AND the
 * server confirming, on this very page load, that the token's round is still
 * active. No token → not a single further byte of work. Round ended → the
 * stale local hint is deleted and nothing renders. The stored flag can never
 * outvote the server.
 */
function boot(): void {
  const script = readScriptConfig()
  if (!script) {
    console.warn('[spotnote] no data-key on the script tag — widget not started')
    return
  }
  if (document.getElementById(HOST_ID)) return

  const reviewToken = readReviewToken()
  if (!reviewToken) return

  const api = createApi(script.base, script.key, reviewToken)

  void api.config().then((config) => {
    // Network trouble: stay invisible but keep the token — the next page load
    // asks again. The host page must never notice us failing.
    if (!config) return

    if (!config.active) {
      // The server said no with authority. A dead token would otherwise cost a
      // wasted API call on every future page load, so drop it — except for
      // 'paused', which is temporary by design.
      if (config.reason !== 'paused') forgetReviewToken()
      return
    }

    try {
      mount(script.base, api, config)
    } catch (cause) {
      console.warn('[spotnote] widget failed to start', cause)
      api.report('widget_error', 'mount_failed', String(cause))
    }
  })
}

function mount(base: string, api: Api, config: WidgetConfig): void {
  // Errors thrown by our own bundles (w.js and its lazy modules) — never the
  // host page's, which are none of our business.
  window.addEventListener('error', (event) => {
    if (!event.filename?.startsWith(`${base}/w`)) return
    api.report(
      'widget_error',
      'exception',
      `${event.message} @ ${event.filename.slice(base.length)}:${event.lineno}`,
    )
  })

  // Before anything renders: a remembered choice or the browser language.
  const locale = detectWidgetLocale()
  setWidgetLocale(locale)

  const store = new Store()

  const host = h('div', { id: HOST_ID })
  const root = host.attachShadow({ mode: 'open' })

  const sheet = new CSSStyleSheet()
  sheet.replaceSync(STYLES)
  root.adoptedStyleSheets = [sheet]

  const overlay = new Overlay(root)
  // Everything that is not an annotation — launcher, chrome, dialogs.
  const chrome = h('div')
  root.appendChild(chrome)

  document.body.appendChild(host)
  overlay.attachScrollSync()

  let captureStarted: Promise<SessionCapture> | null = null
  let composerNode: HTMLElement | null = null
  let composerKey: ComposerTarget | null = null

  const openComposer = (target: ComposerTarget) => {
    store.set({ composer: target, draft: null })
  }

  const saveNote = (comment: string) => {
    const target = store.get().composer
    if (!target) return

    const trimmed = comment.trim()
    if (!trimmed) return

    const geometry =
      target.type === 'pin'
        ? { type: 'pin' as const, x: target.x, y: target.y }
        : target.type === 'rect'
          ? { type: 'rect' as const, x: target.x, y: target.y, w: target.w, h: target.h }
          : { type: 'draw' as const, points: target.points }

    const { cx, cy } = centroid(geometry)

    const note: LocalNote = {
      id: `${Date.now()}-${Math.round(Math.random() * 1e6)}`,
      n: store.get().notes.length + 1,
      ...geometry,
      cx: Math.round(cx),
      cy: Math.round(cy),
      comment: trimmed,
      targets: target.targets,
      scroll_y: Math.round(window.scrollY),
      viewport_w: window.innerWidth,
      viewport_h: window.innerHeight,
    }

    store.set((state) => ({ notes: [...state.notes, note], composer: null }))
  }

  const startFeedback = () => {
    store.set({ active: true, tool: 'pin', sentOverlay: false, error: null })
    // Snapshot the page as it looks right now, before any mark is drawn.
    if (!captureStarted) {
      captureStarted = captureSession(api, base)
      // Reported now, not with the session: a visitor who gives up and never
      // sends would otherwise take the failure with them.
      void captureStarted.then((capture) => {
        if (capture.error) api.report('capture_failed', capture.error)
      })
    }
  }

  const exitFeedback = () => {
    store.set({ active: false, tool: null, draft: null, composer: null, reviewing: false })
  }

  const send = async () => {
    const state = store.get()
    if (state.sending || state.notes.length === 0) return

    store.set({ sending: true, error: null })

    try {
      const capture = await captureStarted
      // The image is a nice-to-have; never make someone wait on it to send.
      if (capture) await withDeadline(capture.raster, RASTER_WAIT_MS)

      await api.session({
        ...pageMetrics(),
        ...(capture?.captureId ? { capture_id: capture.captureId } : {}),
        ...(capture?.error ? { capture_error: capture.error } : {}),
        notes: toWirePayload(state.notes),
      })

      captureStarted = null
      store.set({
        sending: false,
        sent: true,
        sentOverlay: true,
        sentCount: state.notes.length,
        reviewing: false,
        active: false,
        tool: null,
        notes: [],
      })
    } catch (cause) {
      console.error('[spotnote] send failed', cause)
      // A refusal is recorded by the server too; a network failure only here.
      api.report(
        'send_failed',
        cause instanceof ApiError ? cause.code : 'network',
        cause instanceof ApiError ? `HTTP ${cause.status}` : String(cause),
      )
      // The round can end while the page sits open. The next API contact —
      // this one — is where the widget learns about it: explain, and stop
      // pretending the token is good.
      const gone =
        cause instanceof ApiError &&
        (cause.code === 'round_ended' || cause.code === 'revoked' || cause.status === 410)
      if (gone) forgetReviewToken()
      store.set({
        sending: false,
        error: gone ? t().errorEnded : t().errorSend,
      })
    }
  }

  // ------------------------------------------------------------------ render

  const render = () => {
    const state = store.get()
    overlay.render(state)

    const parts: HTMLElement[] = []

    if (state.active) {
      parts.push(h('div', { class: 'ring' }))
      parts.push(h('div', { class: 'hint', text: hintText(state) }))

      if (state.tool && !state.composer && !state.reviewing) {
        parts.push(createCaptureSurface(store, openComposer))
      }

      parts.push(
        renderToolbar(state, {
          arm: (tool: NoteType) => store.set({ tool, composer: null, draft: null }),
          undo: () => store.set((s) => ({ notes: s.notes.slice(0, -1) })),
          review: () => store.set({ reviewing: true, tool: null, composer: null, draft: null }),
          setLocale: (next: WidgetLocale) => {
            setWidgetLocale(next, { persist: true })
            store.set({ locale: next })
          },
          exit: exitFeedback,
        }),
      )
    }

    if (state.reviewing) {
      const panel = renderReview(state, {
        back: () => store.set({ reviewing: false, tool: 'pin' }),
        send: () => void send(),
      })
      if (state.error) {
        panel.querySelector('.panel-body')?.prepend(h('div', { class: 'error', text: state.error }))
      }
      parts.push(panel)
    }

    if (state.sentOverlay) {
      parts.push(
        renderSent(state.sentCount, state.config?.brand.label ?? null, () =>
          store.set({ sentOverlay: false }),
        ),
      )
    }

    if (!state.active && !state.reviewing && !state.sentOverlay) {
      const launcher = renderLauncher(state, startFeedback)
      if (launcher) parts.push(launcher)
    }

    replace(chrome, ...parts)

    // The composer is mounted separately and only rebuilt when it changes, so
    // typing never destroys the textarea underneath the caret.
    if (state.composer !== composerKey) {
      composerNode?.remove()
      composerNode = null
      composerKey = state.composer

      if (state.composer) {
        composerNode = renderComposer(state.composer, state.notes.length + 1, {
          save: saveNote,
          cancel: () => store.set({ composer: null, draft: null }),
        })
        overlay.mount(composerNode)
      }
    }
  }

  store.subscribe(render)

  // Saved notes, or a note half-typed in the composer: either would vanish
  // with the page, so leaving asks first.
  attachUnsavedGuard(() => {
    if (store.get().notes.length > 0) return true
    const typed = composerNode?.querySelector('textarea')?.value.trim() ?? ''
    return typed !== ''
  })

  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return
    const state = store.get()
    if (state.composer) store.set({ composer: null, draft: null })
    else if (state.reviewing) store.set({ reviewing: false })
    else if (state.tool) store.set({ tool: null, draft: null })
    else if (state.active) exitFeedback()
  })

  store.set({ config, locale })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true })
} else {
  boot()
}
